'use client';

import Tooltip from 'rc-tooltip';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { InfoButton } from '@/components/buttons/InfoButton';
import { ImageComponent } from '@/components/Image';
import { MediaType } from '@/helpers/enums';
import { useResponsive } from '@/modules/client';
import { useGetCommonStore } from '@/store';
import { formatCompPoints } from '../../helpers';
import { BonusShopBalanceSkeleton } from '../skeletons/BonusShopBalanceSkeleton';
import {
  overlayStyle,
  ScBonusShopBalance,
  ScBonusShopBalanceTooltip,
} from './styled';

const COIN_SIZE = 32;
const TOOLTIP_ALIGN = { offset: [12, 17] };

interface BonusShopBalanceProps {
  onTermsClick?: () => void;
}

export const BonusShopBalance = ({ onTermsClick }: BonusShopBalanceProps) => {
  const { t } = useTranslation('bonusShop');
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const compPoints = useCommonStore.use.compPoints();
  const compPointsPending = useCommonStore.use.compPointsPending();
  const pendingUser = useCommonStore.use.pendingUser();

  const { grayDarkColor, whiteColor } = useTheme();
  const { currentMediaType } = useResponsive();
  const isTapTrigger = currentMediaType <= MediaType.TABLET;

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const overlayInnerStyle = useMemo(
    () => ({
      background: grayDarkColor,
      width: '216px',
      borderRadius: '12px',
      border: 'none',
      padding: '12px',
      color: whiteColor,
    }),
    [grayDarkColor, whiteColor]
  );

  const balance = compPoints?.chargeable?.points || 0;

  if (!isAuthenticated) return null;

  if (compPointsPending || pendingUser || !compPoints)
    return <BonusShopBalanceSkeleton />;

  return (
    <ScBonusShopBalance>
      <ImageComponent
        alt="CP"
        height={COIN_SIZE}
        src="/common/cp-coin.webp"
        width={COIN_SIZE}
      />

      <div className="info">
        <span className="label">{t('balance')}</span>
        <span className="value-row">
          <span className="dot" />
          <span className="value">{formatCompPoints(balance)}</span>
        </span>
      </div>

      <Tooltip
        align={TOOLTIP_ALIGN}
        onVisibleChange={setIsTooltipVisible}
        overlay={
          <ScBonusShopBalanceTooltip>
            <p className="tooltip-title">{t('tooltip.title')}</p>
            <p className="tooltip-text">{t('tooltip.text')}</p>
            {onTermsClick && (
              <button
                className="tooltip-link"
                onClick={() => {
                  setIsTooltipVisible(false);
                  onTermsClick();
                }}
                type="button"
              >
                {t('tooltip.link')}
              </button>
            )}
          </ScBonusShopBalanceTooltip>
        }
        overlayInnerStyle={overlayInnerStyle}
        overlayStyle={overlayStyle}
        placement="bottomRight"
        showArrow={false}
        trigger={isTapTrigger ? ['click'] : ['hover']}
        visible={isTooltipVisible}
      >
        <InfoButton />
      </Tooltip>
    </ScBonusShopBalance>
  );
};
